/* SPECULUM — Sigil geometry
   Geometric primitives only: circles, lines, triangles, squares.
   Each sigil is a composed glyph — no hand-drawn pictorial SVG. */

const { useMemo } = React;

// Master ritual diagram — composed of overlapping circles + radials
function MasterSigil({ size = 220, color = "currentColor", spin = false }) {
  const c = size / 2;
  const r1 = size * 0.46;
  const r2 = size * 0.34;
  const r3 = size * 0.22;
  // 12 radial ticks
  const ticks = [];
  for (let i = 0; i < 24; i++) {
    const a = (i / 24) * Math.PI * 2;
    const inner = i % 2 === 0 ? r1 * 0.94 : r1 * 0.97;
    ticks.push({
      x1: c + Math.cos(a) * inner, y1: c + Math.sin(a) * inner,
      x2: c + Math.cos(a) * r1, y2: c + Math.sin(a) * r1,
    });
  }
  // 6-pointed hex of small circles on r2
  const nodes = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
    nodes.push({ x: c + Math.cos(a) * r2, y: c + Math.sin(a) * r2 });
  }
  // connecting lines between opposite nodes
  const diag = [];
  for (let i = 0; i < 3; i++) {
    diag.push([nodes[i], nodes[i + 3]]);
  }
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ color, maxWidth: "100%", height: "auto" }} className={spin ? "spin-slow" : ""}>
      {/* outer ring */}
      <circle cx={c} cy={c} r={r1} fill="none" stroke="currentColor" strokeWidth="1" />
      <circle cx={c} cy={c} r={r1 * 0.97} fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.5" />
      {/* ticks */}
      {ticks.map((t, i) => (
        <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke="currentColor" strokeWidth="0.6" />
      ))}
      {/* mid ring */}
      <circle cx={c} cy={c} r={r2} fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.7" />
      {/* inner triangle pair (hexagram) */}
      <polygon
        points={nodes.map(n => `${n.x},${n.y}`).join(" ")}
        fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.85"
      />
      {/* diagonal lines */}
      {diag.map(([a, b], i) => (
        <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="currentColor" strokeWidth="0.5" opacity="0.6" />
      ))}
      {/* nodes */}
      {nodes.map((n, i) => (
        <circle key={i} cx={n.x} cy={n.y} r="3" fill="currentColor" />
      ))}
      {/* core */}
      <circle cx={c} cy={c} r={r3} fill="none" stroke="currentColor" strokeWidth="1" />
      <circle cx={c} cy={c} r={r3 * 0.5} fill="none" stroke="currentColor" strokeWidth="0.5" />
      <circle cx={c} cy={c} r="2" fill="currentColor" />
    </svg>
  );
}

// Brand mark — smaller, denser
function BrandMark({ size = 36, color = "currentColor" }) {
  const c = size / 2;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ color }}>
      <circle cx={c} cy={c} r={size * 0.45} fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx={c} cy={c} r={size * 0.28} fill="none" stroke="currentColor" strokeWidth="0.8" />
      <line x1={c} y1={c - size * 0.45} x2={c} y2={c + size * 0.45} stroke="currentColor" strokeWidth="0.8" />
      <line x1={c - size * 0.45} y1={c} x2={c + size * 0.45} y2={c} stroke="currentColor" strokeWidth="0.8" />
      <circle cx={c} cy={c} r="1.6" fill="currentColor" />
    </svg>
  );
}

// Variant glyphs — used for symbol inventory cards and archive
const GLYPH_PRIMITIVES = [
  // 0: cross-in-circle
  (s) => (
    <>
      <circle cx={s/2} cy={s/2} r={s*0.4} fill="none" stroke="currentColor" strokeWidth="1" />
      <line x1={s*0.18} y1={s/2} x2={s*0.82} y2={s/2} stroke="currentColor" strokeWidth="1" />
      <line x1={s/2} y1={s*0.18} x2={s/2} y2={s*0.82} stroke="currentColor" strokeWidth="1" />
    </>
  ),
  // 1: triangle in circle
  (s) => (
    <>
      <circle cx={s/2} cy={s/2} r={s*0.4} fill="none" stroke="currentColor" strokeWidth="1" />
      <polygon points={`${s/2},${s*0.2} ${s*0.78},${s*0.72} ${s*0.22},${s*0.72}`} fill="none" stroke="currentColor" strokeWidth="1" />
    </>
  ),
  // 2: square diamond
  (s) => (
    <>
      <rect x={s*0.18} y={s*0.18} width={s*0.64} height={s*0.64} fill="none" stroke="currentColor" strokeWidth="1" />
      <polygon points={`${s/2},${s*0.18} ${s*0.82},${s/2} ${s/2},${s*0.82} ${s*0.18},${s/2}`} fill="none" stroke="currentColor" strokeWidth="1" />
    </>
  ),
  // 3: hex w/ center dot
  (s) => {
    const c=s/2, r=s*0.4;
    const pts = Array.from({length:6}, (_,i)=>{
      const a = i/6*Math.PI*2 - Math.PI/2;
      return `${c+Math.cos(a)*r},${c+Math.sin(a)*r}`;
    }).join(" ");
    return (<>
      <polygon points={pts} fill="none" stroke="currentColor" strokeWidth="1" />
      <circle cx={c} cy={c} r={s*0.08} fill="currentColor" />
    </>);
  },
  // 4: concentric three
  (s) => (<>
    <circle cx={s/2} cy={s/2} r={s*0.42} fill="none" stroke="currentColor" strokeWidth="1" />
    <circle cx={s/2} cy={s/2} r={s*0.28} fill="none" stroke="currentColor" strokeWidth="1" />
    <circle cx={s/2} cy={s/2} r={s*0.12} fill="currentColor" />
  </>),
  // 5: X with circle
  (s) => (<>
    <circle cx={s/2} cy={s/2} r={s*0.42} fill="none" stroke="currentColor" strokeWidth="1" />
    <line x1={s*0.22} y1={s*0.22} x2={s*0.78} y2={s*0.78} stroke="currentColor" strokeWidth="1" />
    <line x1={s*0.78} y1={s*0.22} x2={s*0.22} y2={s*0.78} stroke="currentColor" strokeWidth="1" />
  </>),
  // 6: vertical bar + dots
  (s) => (<>
    <line x1={s/2} y1={s*0.15} x2={s/2} y2={s*0.85} stroke="currentColor" strokeWidth="1.5" />
    <circle cx={s*0.3} cy={s/2} r={s*0.06} fill="currentColor" />
    <circle cx={s*0.7} cy={s/2} r={s*0.06} fill="currentColor" />
    <circle cx={s/2} cy={s*0.15} r={s*0.06} fill="currentColor" />
    <circle cx={s/2} cy={s*0.85} r={s*0.06} fill="currentColor" />
  </>),
  // 7: triangle down + bar
  (s) => (<>
    <polygon points={`${s*0.2},${s*0.25} ${s*0.8},${s*0.25} ${s/2},${s*0.78}`} fill="none" stroke="currentColor" strokeWidth="1" />
    <line x1={s*0.3} y1={s*0.4} x2={s*0.7} y2={s*0.4} stroke="currentColor" strokeWidth="1" />
  </>),
  // 8: arrow up in circle
  (s) => (<>
    <circle cx={s/2} cy={s/2} r={s*0.42} fill="none" stroke="currentColor" strokeWidth="1" />
    <line x1={s/2} y1={s*0.25} x2={s/2} y2={s*0.75} stroke="currentColor" strokeWidth="1" />
    <line x1={s*0.34} y1={s*0.38} x2={s/2} y2={s*0.25} stroke="currentColor" strokeWidth="1" />
    <line x1={s*0.66} y1={s*0.38} x2={s/2} y2={s*0.25} stroke="currentColor" strokeWidth="1" />
  </>),
  // 9: nested squares
  (s) => (<>
    <rect x={s*0.18} y={s*0.18} width={s*0.64} height={s*0.64} fill="none" stroke="currentColor" strokeWidth="1" />
    <rect x={s*0.3} y={s*0.3} width={s*0.4} height={s*0.4} fill="none" stroke="currentColor" strokeWidth="0.8" />
    <circle cx={s/2} cy={s/2} r={s*0.06} fill="currentColor" />
  </>),
  // 10: vesica
  (s) => (<>
    <circle cx={s*0.38} cy={s/2} r={s*0.32} fill="none" stroke="currentColor" strokeWidth="1" />
    <circle cx={s*0.62} cy={s/2} r={s*0.32} fill="none" stroke="currentColor" strokeWidth="1" />
  </>),
  // 11: pentagram-light (5 dots in circle)
  (s) => {
    const c=s/2, r=s*0.34;
    const dots = Array.from({length:5}, (_,i)=>{
      const a = i/5*Math.PI*2 - Math.PI/2;
      return [c+Math.cos(a)*r, c+Math.sin(a)*r];
    });
    return (<>
      <circle cx={c} cy={c} r={s*0.42} fill="none" stroke="currentColor" strokeWidth="1" />
      {dots.map(([x,y], i)=>(<circle key={i} cx={x} cy={y} r={s*0.05} fill="currentColor" />))}
      {dots.map(([x,y], i)=>{
        const [x2,y2] = dots[(i+2)%5];
        return <line key={"l"+i} x1={x} y1={y} x2={x2} y2={y2} stroke="currentColor" strokeWidth="0.5" opacity="0.5" />;
      })}
    </>);
  },
];

function Glyph({ index = 0, size = 64, color = "currentColor", className = "" }) {
  const i = ((index % GLYPH_PRIMITIVES.length) + GLYPH_PRIMITIVES.length) % GLYPH_PRIMITIVES.length;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ color }} className={className}>
      {GLYPH_PRIMITIVES[i](size)}
    </svg>
  );
}

// Generated sigil from a string (deterministic) — for archived audits
function GeneratedSigil({ seed = "abc", size = 64, color = "currentColor" }) {
  // simple hash
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const a = Math.abs(h);
  const idx = a % GLYPH_PRIMITIVES.length;
  return <Glyph index={idx} size={size} color={color} />;
}

// Background ritual overlay — single subtle large diagram
function RitualOverlay() {
  return (
    <div className="sigil-overlay" aria-hidden="true">
      <svg viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid meet">
        <g transform="translate(500 500)" stroke="currentColor" fill="none" strokeWidth="0.5">
          <circle r="400" />
          <circle r="320" />
          <circle r="240" />
          <circle r="160" />
          {Array.from({length: 24}).map((_, i)=>{
            const a = i/24*Math.PI*2;
            return <line key={i} x1={Math.cos(a)*160} y1={Math.sin(a)*160} x2={Math.cos(a)*400} y2={Math.sin(a)*400} />;
          })}
          <polygon points={Array.from({length:6}, (_,i)=>{
            const a = i/6*Math.PI*2 - Math.PI/2;
            return `${Math.cos(a)*240},${Math.sin(a)*240}`;
          }).join(" ")} />
          <polygon points={Array.from({length:6}, (_,i)=>{
            const a = i/6*Math.PI*2 + Math.PI/2;
            return `${Math.cos(a)*240},${Math.sin(a)*240}`;
          }).join(" ")} />
        </g>
      </svg>
    </div>
  );
}

// Winged master sigil — used in Angelic Vampire palette
function WingedSigil({ size = 220, color = "currentColor", spin = false }) {
  const c = size / 2;
  const r = size * 0.32;

  // Each wing has:
  //  - a leading-edge top arc from shoulder to wing-tip
  //  - 4 primary flight feathers hanging beneath
  const Wing = ({ side }) => {
    const dir = side === "L" ? -1 : 1;
    const shoulderX = c + dir * size * 0.04;
    const shoulderY = c - size * 0.02;
    const tipX = c + dir * size * 0.46;
    const tipY = c - size * 0.22; // wing tip soars up and out
    const ctrlX = c + dir * size * 0.20;
    const ctrlY = c - size * 0.30; // arc bows upward
    // 4 anchor points along the leading edge for feathers to drop from
    const feathers = [0.30, 0.50, 0.70, 0.88].map((t, i) => {
      // quadratic bezier point at param t
      const omt = 1 - t;
      const ax = omt * omt * shoulderX + 2 * omt * t * ctrlX + t * t * tipX;
      const ay = omt * omt * shoulderY + 2 * omt * t * ctrlY + t * t * tipY;
      // feather drops down-and-outward (toward the trailing edge)
      const reach = size * (0.13 - i * 0.015);
      const angleOut = (15 + i * 8) * Math.PI / 180;  // downward sweep
      const fx = ax + dir * Math.cos(angleOut) * reach;
      const fy = ay + Math.sin(angleOut) * reach;
      return { ax, ay, fx, fy, w: 1.1 - i * 0.18 };
    });
    return (
      <g>
        {/* leading edge arc */}
        <path
          d={`M ${shoulderX} ${shoulderY} Q ${ctrlX} ${ctrlY}, ${tipX} ${tipY}`}
          fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"
        />
        {/* a softer secondary arc beneath, suggesting wing-coverts */}
        <path
          d={`M ${shoulderX + dir * size * 0.02} ${shoulderY + size * 0.02}
              Q ${ctrlX + dir * size * 0.02} ${ctrlY + size * 0.16},
                ${tipX - dir * size * 0.04} ${tipY + size * 0.06}`}
          fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.5"
        />
        {/* primary flight feathers */}
        {feathers.map((f, i) => (
          <line
            key={i}
            x1={f.ax} y1={f.ay} x2={f.fx} y2={f.fy}
            stroke="currentColor" strokeWidth={f.w} strokeLinecap="round"
            opacity={0.92 - i * 0.05}
          />
        ))}
        {/* shoulder dot */}
        <circle cx={shoulderX} cy={shoulderY} r="1.6" fill="currentColor" />
      </g>
    );
  };

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ color, maxWidth: "100%", height: "auto" }} className={spin ? "spin-slow" : ""}>
      {/* outer halo */}
      <circle cx={c} cy={c} r={size * 0.48} fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.35" />
      <circle cx={c} cy={c} r={size * 0.45} fill="none" stroke="currentColor" strokeWidth="1" />
      {/* tick marks on halo */}
      {Array.from({ length: 24 }).map((_, i) => {
        const a = (i / 24) * Math.PI * 2;
        const r1 = size * 0.45;
        const r2 = size * 0.48;
        return <line key={"t"+i} x1={c + Math.cos(a) * r1} y1={c + Math.sin(a) * r1}
                     x2={c + Math.cos(a) * r2} y2={c + Math.sin(a) * r2}
                     stroke="currentColor" strokeWidth="0.5" />;
      })}
      {/* wings */}
      <Wing side="L" />
      <Wing side="R" />
      {/* inner halo around drop */}
      <circle cx={c} cy={c + size * 0.06} r={r * 0.65} fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.45" />
      <circle cx={c} cy={c + size * 0.06} r={r * 0.45} fill="none" stroke="currentColor" strokeWidth="0.4" opacity="0.3" />
      {/* central blood drop */}
      <g transform={`translate(${c} ${c + size * 0.06})`}>
        <path d={`M 0 ${-r * 0.42}
                 Q ${r * 0.28} ${-r * 0.04}, ${r * 0.22} ${r * 0.22}
                 Q 0 ${r * 0.48}, ${-r * 0.22} ${r * 0.22}
                 Q ${-r * 0.28} ${-r * 0.04}, 0 ${-r * 0.42} Z`}
              fill="currentColor" />
        {/* highlight ridge */}
        <path d={`M ${-r * 0.10} ${-r * 0.20}
                 Q ${-r * 0.18} ${-r * 0.02}, ${-r * 0.08} ${r * 0.12}`}
              fill="none" stroke="var(--bg)" strokeWidth="1.2" opacity="0.35" strokeLinecap="round" />
      </g>
    </svg>
  );
}

// Corner flourish — small ornamental SVG for card corners
function CornerFlourish() {
  return (
    <svg viewBox="0 0 28 28" width="28" height="28">
      <path d="M 0 0 L 14 0 M 0 0 L 0 14" stroke="currentColor" strokeWidth="1" fill="none" />
      <path d="M 0 4 Q 6 4, 8 0 M 4 0 Q 4 6, 0 8" stroke="currentColor" strokeWidth="0.6" fill="none" />
      <circle cx="3" cy="3" r="1" fill="currentColor" />
    </svg>
  );
}

// Small inline ornament — used in section dividers, between glyphs
function Ornament({ size = 36 }) {
  return (
    <svg viewBox="0 0 60 16" width={size * 1.7} height={size * 0.45} style={{ display: "block" }}>
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        <line x1="0" y1="8" x2="22" y2="8" />
        <line x1="38" y1="8" x2="60" y2="8" />
        <circle cx="30" cy="8" r="3.2" />
        <circle cx="30" cy="8" r="6" opacity="0.5" />
        <line x1="22" y1="4" x2="22" y2="12" opacity="0.6" />
        <line x1="38" y1="4" x2="38" y2="12" opacity="0.6" />
      </g>
      <circle cx="30" cy="8" r="1.2" fill="currentColor" />
    </svg>
  );
}

// Section corner-flourish pack — applied when ornamentation == baroque
function CornerPack() {
  return (
    <>
      <span className="corner-flourish cf-tl"><CornerFlourish /></span>
      <span className="corner-flourish cf-tr"><CornerFlourish /></span>
      <span className="corner-flourish cf-bl"><CornerFlourish /></span>
      <span className="corner-flourish cf-br"><CornerFlourish /></span>
    </>
  );
}

Object.assign(window, { MasterSigil, WingedSigil, BrandMark, Glyph, GeneratedSigil, RitualOverlay, Ornament, CornerFlourish, CornerPack });
